<template>
  <ElevationPage
    v-model:selectedLocation="selectedLocation"
    :currentLocationSupported="false"
    :location="pageLocation"
    :error="error"
    :loading="pending"
    :elevationStatus="elevationStatus"
  >
    <template #settings-bottom>
      <div flex gap-6 w-full>
        <NuxtLink
          class="text-blue-600 underline font-mono text-lg inline-flex gap-1 items-center hover:text-blue-400"
          :to="`/map?lat=${route.params.latitude}&lng=${route.params.longitude}&title=${encodeURI(routeLocation.name)}`"
        >
          <div class="i-material-symbols-location-on-rounded inline-block"></div>
          View on map
        </NuxtLink>
        <NuxtLink
          class="text-green-600 hover:text-green-400 underline font-mono text-lg inline-flex gap-1 items-center"
          :to="`/peaks?lat=${route.params.latitude}&lng=${route.params.longitude}&title=${encodeURI(
            routeLocation.name
          )}`"
        >
          <div class="i-icon-park-solid-three-triangles inline-block"></div>
          Nearby peaks
        </NuxtLink>
      </div>
    </template>
    <template #below-info>
      <WikipediaInfo v-if="route.params.name" :slug="route.params.name" />
    </template>
  </ElevationPage>
</template>
<script setup>
const route = useRoute();

const routeLocation = computed(() => ({
  latitude: parseFloat(route.params.latitude),
  longitude: parseFloat(route.params.longitude),
  name: decodeUrlTitle(route.params.name) || "Saved Location",
  wiki: route.params.wiki,
}));

useSeoMeta({
  title: () => routeLocation.value.name,
  titleTemplate: "%s - What is my elevation?",
});

defineOgImage({ component: "root" });

const { data: altitude, pending, error } = await useFetchElevation(routeLocation.value);
const elevationStatus = computed(() => {
  if (pending.value) return null;
  const status = elevationLookupStatus(altitude.value, error.value);
  return status === "ok" ? null : status;
});
const pageLocation = computed(() => ({ ...routeLocation.value, altitude: altitude.value, altitudeSource: "web" }));

// the elevation is fetched during server rendering, so search results can show it
const description = computed(() => {
  const name = routeLocation.value.name;
  if (!isNumber(altitude.value)) {
    return `Find the elevation of ${name}, or any point on Earth, in feet and meters.`;
  }
  const feet = numberFormatted(Math.abs(altitude.value) * ELEVATION_UNITS.ft.multiplier);
  const meters = numberFormatted(Math.abs(altitude.value));
  const direction = altitude.value < 0 ? "below" : "above";
  return `The elevation of ${name} is ${feet} feet (${meters} meters) ${direction} sea level. Find the elevation of any point on Earth.`;
});
useSeoMeta({ description, ogDescription: description });
const selectedLocation = ref(pageLocation.value);

watch(selectedLocation, async (selectedLocation) => {
  if (!selectedLocation) {
    return;
  }
  let path;
  if (selectedLocation?.type == "currentLocation") {
    path = "/";
  } else if (selectedLocation?.location) {
    path = {
      name: "location-latitude,longitude-name-wiki",
      params: {
        latitude: round(selectedLocation.location.latitude, 5),
        longitude: round(selectedLocation.location.longitude, 5),
        name: encodeUrlTitle(selectedLocation.location?.name),
      },
    };
  }
  await navigateTo(path);
});
</script>
